import { useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { bottomSheetDockClassName } from "@/lib/bottom-sheet-compact";
import { BarChart3, ClipboardCheck, Upload, FileText, Plus, Loader2, ChevronLeft } from "lucide-react";
import { uploadDealFileClient } from "@/lib/deal-file-upload";
import { queryKeys } from "@/lib/query-keys";

type Panel = "menu" | "note" | "files";

export interface DealFabQuickActionsProps {
  dealId: string;
  measurementProducts?: Array<{ id?: string; productType?: string; index?: number }>;
  onAddNote: (body: string) => Promise<unknown>;
  addNotePending: boolean;
}

export function DealFabQuickActions({
  dealId,
  measurementProducts = [],
  onAddNote,
  addNotePending,
}: DealFabQuickActionsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<Panel>("menu");
  const [noteBody, setNoteBody] = useState("");
  const [noteError, setNoteError] = useState<string | null>(null);
  const [uploadProductItemId, setUploadProductItemId] = useState("");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pickedFiles, setPickedFiles] = useState<File[]>([]);

  const productOptions = useMemo(
    () => [
      { id: "", label: t("deal.filesDealGeneral") },
      ...(measurementProducts || [])
        .filter((p) => p.id)
        .map((p) => ({
          id: p.id!,
          label: `${p.productType ?? t("deal.product")} ${p.index ?? ""}`.trim(),
        })),
    ],
    [measurementProducts, t],
  );

  const resetPanels = () => {
    setPanel("menu");
    setNoteBody("");
    setNoteError(null);
    setUploadError(null);
    setPickedFiles([]);
    setUploadProductItemId("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      resetPanels();
    }
  };

  const handleSubmitNote = async () => {
    const body = noteBody.trim();
    if (!body) {
      setNoteError(t("deal.fabNoteEmpty"));
      return;
    }
    setNoteError(null);
    try {
      await onAddNote(body);
      handleOpenChange(false);
    } catch (e) {
      setNoteError(e instanceof Error ? e.message : t("deal.fabNoteFailed"));
    }
  };

  const handleUpload = async () => {
    if (pickedFiles.length === 0) {
      setUploadError(t("deal.fabPickFiles"));
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      for (const file of pickedFiles) {
        await uploadDealFileClient(dealId, file, uploadProductItemId || null);
      }
      await qc.invalidateQueries({ queryKey: ["deal-files", dealId] });
      await qc.invalidateQueries({ queryKey: ["deal", dealId] });
      await qc.invalidateQueries({ queryKey: queryKeys.crmBoard });
      handleOpenChange(false);
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : t("deal.fabUploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  const menuRowClass = cn(
    "flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 py-2 text-left",
    "transition-colors hover:bg-accent hover:text-accent-foreground text-foreground",
  );
  const menuIconWrap = "flex h-9 w-9 shrink-0 items-center justify-center text-primary";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "fixed z-40",
          "h-14 w-14 rounded-full",
          "bg-primary text-primary-foreground",
          "shadow-lg hover:shadow-xl",
          "flex items-center justify-center",
          "transition-all duration-200",
          "active:scale-95",
        )}
        style={{
          bottom: "calc(4rem + env(safe-area-inset-bottom) + var(--fab-bottom-offset, 1rem))",
          right: "max(1rem, env(safe-area-inset-right))",
        }}
        aria-label={t("common.quickActions")}
      >
        <Plus className="h-6 w-6" />
      </button>

      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          className={bottomSheetDockClassName("max-h-[90dvh] overflow-y-auto")}
        >
          <div className="mx-auto mb-2 h-1 w-10 shrink-0 rounded-full bg-muted" aria-hidden />

          {panel === "menu" ? (
            <>
              <SheetHeader className="max-[767px]:pt-0 space-y-0 border-b px-0 pb-2 pt-0 text-left">
                <SheetTitle className="text-base font-semibold leading-snug">
                  {t("common.quickActions")}
                </SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-0.5 pb-3">
                <button
                  type="button"
                  onClick={() => {
                    handleOpenChange(false);
                    navigate(`/crm/measure/${dealId}`);
                  }}
                  className={menuRowClass}
                >
                  <div className={menuIconWrap}>
                    <BarChart3 className="h-5 w-5" />
                  </div>
                  <span className="text-sm font-medium leading-snug">{t("sidebar.measure")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleOpenChange(false);
                    navigate(`/crm/handover/${dealId}`);
                  }}
                  className={menuRowClass}
                >
                  <div className={menuIconWrap}>
                    <ClipboardCheck className="h-5 w-5" />
                  </div>
                  <span className="text-sm font-medium leading-snug">{t("sidebar.handover")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPanel("files");
                    setUploadError(null);
                  }}
                  className={menuRowClass}
                >
                  <div className={menuIconWrap}>
                    <Upload className="h-5 w-5" />
                  </div>
                  <span className="text-sm font-medium leading-snug">{t("deal.uploadFiles")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPanel("note");
                    setNoteError(null);
                  }}
                  className={menuRowClass}
                >
                  <div className={menuIconWrap}>
                    <FileText className="h-5 w-5" />
                  </div>
                  <span className="text-sm font-medium leading-snug">{t("deal.addNote")}</span>
                </button>
              </div>
            </>
          ) : panel === "note" ? (
            <div className="space-y-4 pb-3">
              <div className="flex items-center gap-2">
                <Button type="button" variant="ghost" size="icon" onClick={() => setPanel("menu")} aria-label={t("deal.back")}>
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <SheetTitle className="text-left flex-1">{t("deal.addNote")}</SheetTitle>
              </div>
              <textarea
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
                placeholder={t("deal.newNotePlaceholder")}
                className="w-full min-h-[120px] px-3 py-2 rounded-lg border bg-background text-sm"
                rows={4}
              />
              {noteError ? <p className="text-sm text-destructive">{noteError}</p> : null}
              <Button type="button" className="w-full" disabled={addNotePending} onClick={() => void handleSubmitNote()}>
                {addNotePending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("deal.addActivity")}
              </Button>
            </div>
          ) : (
            <div className="space-y-4 pb-3">
              <div className="flex items-center gap-2">
                <Button type="button" variant="ghost" size="icon" onClick={() => setPanel("menu")} aria-label={t("deal.back")}>
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <SheetTitle className="text-left flex-1">{t("deal.uploadFiles")}</SheetTitle>
              </div>
              {productOptions.length > 1 ? (
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">{t("dealExtrasExtra.uploadLocation")}</label>
                  <select
                    value={uploadProductItemId}
                    onChange={(e) => setUploadProductItemId(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border bg-background text-sm"
                  >
                    {productOptions.map((opt) => (
                      <option key={opt.id || "deal"} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div>
                <label className="block text-sm text-muted-foreground mb-2">{t("deal.fabChooseFiles")}</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="w-full text-sm"
                  onChange={(e) => {
                    const list = e.target.files;
                    setPickedFiles(list ? Array.from(list) : []);
                    setUploadError(null);
                  }}
                />
                {pickedFiles.length > 0 ? (
                  <ul className="mt-2 text-sm text-muted-foreground space-y-1">
                    {pickedFiles.map((f) => (
                      <li key={f.name + f.size} className="truncate">
                        {f.name}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
              {uploadError ? <p className="text-sm text-destructive">{uploadError}</p> : null}
              <Button type="button" className="w-full" disabled={uploading || pickedFiles.length === 0} onClick={() => void handleUpload()}>
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("deal.fabUpload")}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
